import {
  call, delay, put, select, takeLatest,
} from 'redux-saga/effects';
import { getWeightedCenter } from '~/_shared/utils/gis/weightedCenter.helpers';
import {
  getSpreadsheetBulkData, type SpreadsheetDataBulkRequest, type SpreadsheetDataBulkResponse, type SpreadsheetDataErrorResponse,
} from '~/spreadsheet/spreadsheet.repository';
import { isSuccessBulkSpreadsheet } from '~/spreadsheet/typeguards';
import { primarySpreadsheetIdSelector } from '~/store/mapInfo/mapInfo.selectors';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { createAreaRequestGetterFilter } from '~/store/spreadsheetData/area/spreadsheetData.area.helpers';
import {
  type FilterTreeItemRequest, mergeFilterTrees,
} from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { spreadsheetDataEntityFiltersSelector } from '~/store/spreadsheetData/filtering/useSpreadsheetDataEntityFilters';
import { getStoreFilterTree } from '~/store/spreadsheetData/filterTree/spreadsheetDataFilterTree.helpers';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';
import {
  weightedCenterOnResultsError,
  weightedCenterSetResults, weightedCenterSetSelectedEntities,
  weightedCenterSetWeightColumnId,
} from './weightedCenter.actionCreators';
import {
  weightedCenterSelectedEntitiesSelector, weightedCenterWeightColumnIdSelector,
} from './weightedCenter.selectors';

export function* weightedCenterSagas() {
  yield takeLatest([
    weightedCenterSetSelectedEntities,
    weightedCenterSetWeightColumnId,
  ], onWeightedCenterSelectedEntitiesChanged);
}

function *onWeightedCenterSelectedEntitiesChanged() {
  // Debounce rapid selection changes: takeLatest cancels this task whenever a new
  // change arrives, so we only continue once none has occurred for 1500ms.
  yield delay(1500);

  const entities: ReturnType<typeof weightedCenterSelectedEntitiesSelector> = yield select(weightedCenterSelectedEntitiesSelector);
  const filter: ReturnType<typeof spreadsheetDataEntityFiltersSelector> = yield select(spreadsheetDataEntityFiltersSelector, entities);

  if (filter.isEmpty) {
    return;
  }

  try {
    const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);
    const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
    const spreadsheetId: ReturnType<typeof primarySpreadsheetIdSelector> = yield select(primarySpreadsheetIdSelector);
    const weightColumnId: ReturnType<typeof weightedCenterWeightColumnIdSelector> = yield select(weightedCenterWeightColumnIdSelector);

    if (!clientId || !mapId || !spreadsheetId) {
      return;
    }

    const entitiesFilters = createAreaRequestGetterFilter({
      filtersCombine: 'or',
      spreadsheetId,
      multiPolygons: filter.multiPolygons,
      circles: filter.circles,
      boundaries: filter.boundaries,
      boundaryTerritories: filter.boundaryTerritories,
    });

    const mapSettingsFilterTree: FilterTreeItemRequest | null = yield call(getStoreFilterTree);

    const request: SpreadsheetDataBulkRequest = {
      params: [{
        map_id: mapId,
        spreadsheet_id: spreadsheetId,
        filter: {
          filter_tree: mergeFilterTrees([entitiesFilters.filter_tree, mapSettingsFilterTree?.filterTree], 'and') || undefined,
          ...entitiesFilters,
          only_get_filtered: true,
        },
        ...(weightColumnId ? { columns_to_fetch: { [DataType.NUMBER]: { [weightColumnId]: { empty_is_null: true } } } } : {}),
      }],
    };

    const spreadsheetData: {
      readonly data: ReadonlyArray<SpreadsheetDataErrorResponse | SpreadsheetDataBulkResponse>;
    } = yield call(
      getSpreadsheetBulkData,
      clientId,
      request,
    );

    if (isSuccessBulkSpreadsheet(spreadsheetData.data[0])) {
      const response: SpreadsheetDataBulkResponse = spreadsheetData.data[0];
      const rowsData = (response.result.basic_data || []).map(row => ({
        lat: +row.latitude,
        lng: +row.longitude,
        weight: weightColumnId
          ? (response.result?.columns_to_fetch?.[DataType.NUMBER]?.[weightColumnId]?.row_values?.[row.row_id] ?? 0)
          : 1,
      }));

      const calculatedWeightedCenter = getWeightedCenter(rowsData);

      yield put(weightedCenterSetResults({
        data: rowsData,
        center: calculatedWeightedCenter,
      }));
    }
    else {
      throw new Error('Failed to fetch spreadsheet data for weighted center');
    }
  }
  catch (error) {
    console.error('Error fetching spreadsheet data for weighted center', error);
    yield put(weightedCenterOnResultsError());
  }
}
